/**
 * Shared bulk component (gap 1 PR 2, D-18 item 7). OPEN-213 reuses it; keep it free of domain
 * logic.
 *
 * Preview, then apply (T-gap1-46). preview(req) POSTs `{ request }` and keeps the server's
 * `{ token, issuedAt }` with that request; apply() POSTs `{ request, token, issuedAt }`, so the
 * server applies exactly what was previewed or refuses. Apply is refused here while any row is
 * ERROR. A 409 PRICE_PREVIEW_STALE means the prices moved since the preview: status 'stale', the
 * rows stay up for reference, and the user previews again. A 400 shows the API's reason. The API
 * is the gate; this makes no trust decisions.
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';

export type PreviewApplyStatus =
  | 'idle'
  | 'previewing'
  | 'ready'
  | 'applying'
  | 'done'
  | 'stale'
  | 'error';

export const STALE_PREVIEW_MESSAGE = 'Prices changed since the preview. Preview again.';

interface PreviewResponse<Row> {
  readonly rows: Row[];
  readonly counts: Record<string, number>;
  readonly token: string;
  readonly issuedAt: string;
}

interface Preview<Req> {
  readonly request: Req;
  readonly token: string;
  readonly issuedAt: string;
}

type Outcome<T> =
  | { readonly ok: true; readonly body: T }
  | { readonly ok: false; readonly stale: boolean; readonly message: string };

/** The API's reason for a refusal: the first field reason, else its message, else the status. */
async function refusal(res: Response): Promise<{ stale: boolean; message: string }> {
  let body: { error?: { code?: string; message?: string; fields?: { reason?: string }[] } } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    // Not JSON: fall back to the status.
  }
  const code = body.error?.code;
  if (res.status === 409 && (code === undefined || code === 'PRICE_PREVIEW_STALE')) {
    return { stale: true, message: STALE_PREVIEW_MESSAGE };
  }
  if (res.status === 403) return { stale: false, message: 'Your role cannot change these prices.' };
  const message =
    body.error?.fields?.[0]?.reason ?? body.error?.message ?? `Failed (${String(res.status)}).`;
  return { stale: false, message };
}

export function usePreviewApply<Req, Row extends { readonly status: string }, Res = unknown>(p: {
  previewPath: string;
  applyPath: string;
  invalidate: readonly (readonly unknown[])[];
}): {
  preview(req: Req): Promise<void>;
  apply(): Promise<void>;
  rows: Row[] | null;
  counts: Record<string, number> | null;
  status: PreviewApplyStatus;
  error: string | null;
  /** True when a preview is up, has no ERROR row, and nothing is in flight. */
  canApply: boolean;
  /** The apply response once status is 'done'. */
  result: Res | null;
  reset(): void;
} {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const [rows, setRows] = React.useState<Row[] | null>(null);
  const [counts, setCounts] = React.useState<Record<string, number> | null>(null);
  const [status, setStatus] = React.useState<PreviewApplyStatus>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<Res | null>(null);
  // The latest preview's request + token, and a counter so a slower older call can't win.
  const kept = React.useRef<Preview<Req> | null>(null);
  const seq = React.useRef(0);
  const props = React.useRef(p);
  props.current = p;

  const post = React.useCallback(
    async <T>(path: string, body: unknown): Promise<Outcome<T>> => {
      let res: Response;
      try {
        res = await authedFetch(opts, path, { method: 'POST', body: JSON.stringify(body) });
      } catch {
        return { ok: false, stale: false, message: 'Could not reach the server. Try again.' };
      }
      if (!res.ok) return { ok: false, ...(await refusal(res)) };
      return { ok: true, body: (await res.json()) as T };
    },
    [opts],
  );

  const preview = React.useCallback(
    async (req: Req): Promise<void> => {
      const mine = ++seq.current;
      kept.current = null;
      setStatus('previewing');
      setError(null);
      setResult(null);
      const out = await post<PreviewResponse<Row>>(props.current.previewPath, { request: req });
      if (mine !== seq.current) return;
      if (!out.ok) {
        setRows(null);
        setCounts(null);
        setStatus('error');
        setError(out.message);
        return;
      }
      kept.current = { request: req, token: out.body.token, issuedAt: out.body.issuedAt };
      setRows(out.body.rows);
      setCounts(out.body.counts);
      setStatus('ready');
    },
    [post],
  );

  const hasError = rows !== null && rows.some((r) => r.status === 'ERROR');
  // 'ready' is only ever set together with a kept preview.
  const canApply = status === 'ready' && !hasError;

  const apply = React.useCallback(async (): Promise<void> => {
    const prev = kept.current;
    if (!canApply || prev === null) return;
    const mine = ++seq.current;
    setStatus('applying');
    setError(null);
    const out = await post<Res>(props.current.applyPath, {
      request: prev.request,
      token: prev.token,
      issuedAt: prev.issuedAt,
    });
    if (mine !== seq.current) return;
    if (!out.ok) {
      // A stale preview can't be applied again; the user must preview again.
      if (out.stale) kept.current = null;
      setStatus(out.stale ? 'stale' : 'error');
      setError(out.message);
      return;
    }
    kept.current = null;
    setResult(out.body);
    setStatus('done');
    for (const key of props.current.invalidate) {
      void qc.invalidateQueries({ queryKey: [...key] });
    }
  }, [canApply, post, qc]);

  const reset = React.useCallback((): void => {
    seq.current++;
    kept.current = null;
    setRows(null);
    setCounts(null);
    setStatus('idle');
    setError(null);
    setResult(null);
  }, []);

  return { preview, apply, rows, counts, status, error, canApply, result, reset };
}
